<script setup lang="tsx">
import type { AddModule } from '@/models/api-generated'
import http from '@/services/http'
import { computed, onBeforeMount, ref, watch } from 'vue'
import sliderStore from '@/stores/slider'
import { newMethodForm, newModuleForm, viewing } from '@/services/utils'
import CancelSaveButtons from '@/components/widgets/CancelSaveButtons.vue'
import ModuleTabs from '../ModuleTabs.vue'
import ModuleForm from '../ModuleForm.vue'
import MethodsForm from '../MethodsForm.vue'
import { moduleRefreshCallback } from '../shared'
import { useModuleFormValidation } from '@/composables/useModuleFormValidation'

const slider = sliderStore()

const { formRef, activeTab, validateForm } = useModuleFormValidation()

const module = ref(newModuleForm())
const methods = ref([newMethodForm()])
const originalModule = ref(newModuleForm())
const originalMethods = ref([newMethodForm()])

const canEditInstance = computed(() => viewing.value === 0)

watch(
	() => module.value,
	() => {
		slider.setUnsavedChanges(slider.hasUnsavedChanges(originalModule, module))
	},
	{ deep: true, immediate: false },
)

watch(
	() => methods.value,
	() => {
		slider.setUnsavedChanges(slider.hasUnsavedChanges(originalMethods, methods))
	},
	{ deep: true, immediate: false },
)

async function cancel() {
	const doCancel = async () => {
		slider.close()
	}
	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		doCancel()
	}
}

onBeforeMount(() => {
	slider.setFooter(<CancelSaveButtons saveId="save-module" cancelId="cancel-module" onCancel={() => cancel()} onSave={triggerSubmit} />)
})

const triggerSubmit = () => {
	queueMicrotask(() => {
		formRef.value?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
	})
}

const submit = async () => {
	if (!(await validateForm())) return

	const payload: AddModule = {
		name: module.value.name,
		description: module.value.description,
		type: module.value.type,
		meta: {
			instance: module.value.instance,
			responsible: module.value.responsible,
			active: module.value.active,
			urlReferences: module.value.urlReferences,
			documentReferences: module.value.documentReferences,
		},
		methods: methods.value.map((m) => ({
			rules: m.rules,
			confidentiality: m.confidentiality,
			integrity: m.integrity,
			availability: m.availability,
			type: m.type,
			description: m.description,
		})),
	}

	await http.post<AddModule>('api/module/add', payload)

	moduleRefreshCallback.value?.()
	slider.setUnsavedChanges(false)
	slider.close()
}
</script>

<template>
	<form ref="formRef" @submit.prevent="submit" class="needs-validation" novalidate>
		<ModuleTabs :override="activeTab">
			<template #module>
				<ModuleForm v-model="module" mode="add" :canEditInstance="canEditInstance"></ModuleForm>
			</template>
			<template #measures>
				<MethodsForm v-model="methods"></MethodsForm>
			</template>
		</ModuleTabs>
	</form>
</template>
